import { AntTag, Icons, Progress, Text } from '@agro-club/agroclub-shared'
import { useAProgress } from 'hooks/useAProgress'
import useFormatDate from 'hooks/useFormatDate'
import { useNotification } from 'hooks/useNotification'
import { refetchFunc } from 'modules/domain/common/hooks'
import { DashboardDeal, DashboardTask } from 'modules/domain/dashboard/types'
import { DealSource } from 'modules/domain/deal/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import formatPrice from 'modules/utils/numbers/formatPrice'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { AButton } from 'views/components/Analytics'
import { CardBidStatus } from './CardBidStatus'
import {
  CardHeaderInfo,
  CardHeaderRow,
  CardHeaderSubtitle,
  CardHeaderTags,
  CardHeaderTitle,
  CardHeaderWrapper,
  MatchesCardHeaderWrapper,
  Until,
} from './styled'
import { CommonInfo } from '../CardItems/styled'
import { TextCard } from 'views/styled/ItemCardStyles'
import { Badge } from 'views/components/Badge/Badge'
import formatNumber from 'modules/utils/numbers/formatNumber'

type Props = {
  task: DashboardTask
  deal?: DashboardDeal
  isOverdue?: boolean
  refetch: refetchFunc
}

const WrappedCardHeaderTags = styled(CardHeaderTags)`
  flex-wrap: wrap;
  row-gap: 8px;
`

const CompleteButton = styled(AButton)`
  margin-bottom: -10px;
`
const MatchesCompleteButton = styled(AButton)`
  position: absolute;
  top: 8px;
  right: 12px;
`

const MarginContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const CardHeader: FC<Props> = ({ task, isOverdue, refetch, deal }) => {
  const { t } = useTranslation(['common', 'task', 'dashboard'])
  const formatDate = useFormatDate()
  const notify = useNotification()
  const bid = task.linked_bid
  const isMatchingSource = deal?.source === DealSource.matching

  const [progress, complete] = useAProgress(() => apiClient.post(endpoints.completeTask(task.id)), {
    eventName: 'completeTask',
    analyticsPayload: {
      id: task.id,
    },
    onSuccess: () => {
      refetch(true)
      notify(Progress.SUCCESS, { title: t('task:notifyTaskCompleted') })
    },
    onError: () => notify(Progress.ERROR, { title: t('notifyError') }),
  })

  return (
    <>
      {isMatchingSource ? (
        <MatchesCardHeaderWrapper>
          <WrappedCardHeaderTags>
            <AntTag color="white">{task.assigned_team.name}</AntTag>
            {task.trader && <AntTag color="white">{task.trader.last_name}</AntTag>}
          </WrappedCardHeaderTags>
          <MatchesCompleteButton
            id="completeTask"
            color="secondary"
            background="none"
            Icon={Icons.IconDone}
            progress={progress}
            onClick={complete}
            size="big"
          />
          <TextContainer>
            <CommonInfo>
              <TextCard>
                {t('dashboard:dealId')} {deal.id}
              </TextCard>
              <Text typography="bodyMedium" color="secondary">
                {t('dashboard:created')} {formatDate(deal.created, { withTime: true })}
              </Text>
            </CommonInfo>
            <MarginContainer>
              <Text typography="accentLarge">
                {formatNumber(deal.margin_percent, true, { fractionDigits: 1 })}%, {formatPrice(deal.margin, true)}
              </Text>
              <Badge color="green">{t('dashboard:margin')}</Badge>
            </MarginContainer>
            <MarginContainer>
              <Text typography="accentLarge">
                {formatNumber(deal.margin_percent_with_overload, true, { fractionDigits: 1 })}%,{' '}
                {formatPrice(deal.margin_with_overload, true)}
              </Text>
              <Badge color="purple">{t('dashboard:marginWithOverload')}</Badge>
            </MarginContainer>
          </TextContainer>
        </MatchesCardHeaderWrapper>
      ) : (
        <CardHeaderWrapper>
          <CardHeaderRow>
            <WrappedCardHeaderTags>
              <AntTag color="white">{task.assigned_team.name}</AntTag>
              {!!bid && <CardBidStatus bid={bid} />}
            </WrappedCardHeaderTags>

            <CompleteButton
              id="completeTask"
              color="secondary"
              background="none"
              Icon={Icons.IconDone}
              progress={progress}
              onClick={complete}
            />
          </CardHeaderRow>
          <CardHeaderInfo>
            <CardHeaderTitle>{task.subject}</CardHeaderTitle>
            <CardHeaderSubtitle>
              <Until $red={isOverdue}>
                {t('common:until')} {formatDate(task?.deadline, { withTime: true })}
              </Until>
              &nbsp;
            </CardHeaderSubtitle>
          </CardHeaderInfo>
        </CardHeaderWrapper>
      )}
    </>
  )
}
